'use client'

import { FlashIcon, GiftIcon, CoinIcon } from '@repo/ui'
import { useClaimInventoryItem } from 'src/features/inventory/hooks'
import type { InventoryItem } from 'src/features/inventory/types/domain'
import { useInventoryActions } from 'src/features/inventory/store'
import { useNotificationActions } from '../../store'
import { inventoryItemToNotification } from '../../factories/notifications.factories'
import type { NotificationItem } from '../../types/domain'

// Test component to trigger various notifications
export function TestNotification() {
  const { addNotification, reset } = useNotificationActions()
  const { addInventoryItem } = useInventoryActions()
  const { claimInventoryItem } = useClaimInventoryItem()
  
  const fakeInventoryItemAdded = () => {
    // Create a fake claimable bonus item
    const fakeBonus: InventoryItem = {
      id: `fake-bonus-${Date.now()}`,
      playerId: 'test-player',
      type: 'bonus',
      status: 'claimable',
      amount: 25,
      currency: 'USD',
      createdAt: new Date().toISOString(),
      applicableGames: [],
      tags: ['test'],
      originType: 'daily_reward',
      originId: 'fake-origin'
    }
    
    // Add to inventory store
    addInventoryItem(fakeBonus)
    
    // Create notification using the factory
    const notificationItem = inventoryItemToNotification(fakeBonus, (key) => key, claimInventoryItem)
    
    // Show sticky notification
    addNotification(notificationItem, true)
    
    console.log('Added fake claimable bonus item:', fakeBonus)
  }
  
  const fakeBoosterItemAdded = () => {
    // Create a fake claimable booster item
    const fakeBooster: InventoryItem = {
      id: `fake-booster-${Date.now()}`,
      playerId: 'test-player',
      type: 'booster',
      status: 'claimable',
      boost: {
        value: 15,
        unit: 'percentage'
      },
      activationType: 'manual',
      createdAt: new Date().toISOString(),
      applicableGames: [],
      tags: ['test'],
      originType: 'daily_reward',
      originId: 'fake-origin'
    }
    
    // Add to inventory store
    addInventoryItem(fakeBooster)
    
    // Create notification using the factory
    const notificationItem = inventoryItemToNotification(fakeBooster, (key) => key, claimInventoryItem)
    
    // Show sticky notification
    addNotification(notificationItem, true)
    
    console.log('Added fake claimable booster item:', fakeBooster)
  }

  const testBoosterNotification: NotificationItem = {
    type: 'inventory',
    icon: <FlashIcon className="w-[22px] h-[22px]" />,
    title: 'New Booster!',
    subtitle: 'Inventory',
    accentColor: 'blue',
    data: {
      reward: '3%',
      tagLabel: 'Booster',
      tagIcon: <FlashIcon className="w-[14px] h-[14px]" />,
    }
  }

  const testBonusNotification: NotificationItem = {
    type: 'inventory',
    icon: <CoinIcon className="w-[22px] h-[22px]" />,
    title: 'Item received',
    subtitle: 'Inventory',
    accentColor: 'green',
    data: {
      reward: '$25',
      tagLabel: 'Free Bet',
      tagIcon: <CoinIcon className="w-[14px] h-[14px]" />,
    },
    cta: {
      label: 'Claim',
      onClick: (closeNotification) => {
        console.log('Test bonus claimed!')
        setTimeout(() => {
          closeNotification()
        }, 1000) // Simulate 1s claim process
      }
    }
  }

  const testRewardNotification: NotificationItem = {
    type: 'reward',
    icon: <CoinIcon className="w-[22px] h-[22px]" />,
    title: 'Reward Received!',
    subtitle: 'Congratulations',
    accentColor: 'yellow',
    data: {
      amount: '100',
      currency: 'coins'
    }
  }

  const testAchievementNotification: NotificationItem = {
    type: 'achievement',
    icon: <GiftIcon className="w-[22px] h-[22px]" />,
    title: 'Achievement Unlocked!',
    subtitle: 'First Win',
    accentColor: 'green',
    data: {
      achievementName: 'First Win',
      points: 50
    }
  }

  const testSystemNotification: NotificationItem = {
    type: 'system',
    title: 'System Message',
    subtitle: 'Important Update',
    accentColor: 'none',
    data: {
      message: 'Server maintenance in 30 minutes',
      severity: 'warning'
    }
  }

  return (
    <div className="fixed bottom-5 left-5 flex gap-2 z-[10000]">
      <button
        className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
        onClick={fakeInventoryItemAdded}
        type="button"
      >
        Fake Bonus
      </button>
      <button
        className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600"
        onClick={fakeBoosterItemAdded}
        type="button"
      >
        Fake Booster
      </button>
      <button
        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        onClick={() => { addNotification(testBoosterNotification); }}
        type="button"
      >
        Test Booster
      </button>
      <button
        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        onClick={() => { addNotification(testBonusNotification, true); }}
        type="button"
      >
        Test Free Bet
      </button>
      <button
        className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
        onClick={() => addNotification(testRewardNotification)}
        type="button"
      >
        Test Reward
      </button>
      <button
        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        onClick={() => addNotification(testAchievementNotification)}
        type="button"
      >
        Test Achievement
      </button>
      <button
        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        onClick={() => addNotification(testSystemNotification)}
        type="button"
      >
        Test System
      </button>
      <button
        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
        onClick={reset}
        type="button"
      >
        Clear All
      </button>
    </div>
  )
}
